/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 25 2024
 **/

import { useCallback, useState } from "react";
import { debounce } from "lodash-es";

import { useChatMessageActions } from "@/frameworks/core/chat";
import { useTheme } from "@emotion/react";
import { Button, Column, Row } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { VoiceCallLayer, MessageList, MessageInput } from "@/modules/chat/components/Common";
import { useEventHandler } from "@rfice/eventhandler";
import { EventID } from "@/frameworks/core/commons";

import { UnauthorizedChannel } from "../../../../../Commons";
import { DeletedChannel } from "../../../../../Commons/DeletedChannel";
import { MessageSearchInConversation, MessageSearchResult } from "../../../../../MiniChatPopup/components";
import { SearchedResultWrapper } from "./emotion";

export default function SpaceChatTab({
	channelData,
	roomID,
	spaceID
}: {
	channelData: WebServices.SpaceChat.Inquiry.Data["rooms"][number];
	roomID: string;
	spaceID: string;
}) {
	const { colors } = useTheme();
	const [isShowMessageSearchResult, setIsShowMessageSearchResult] = useState(false);
	const [searchText, setSearchText] = useState<string>("");
	const [isSearchInputOpened, setIsSearchInputOpened] = useState(false);

	useEventHandler(
		EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT,
		({ roomId: targetRoomId }: { roomId: string }) => {
			if (targetRoomId !== roomID) return;
			setIsSearchInputOpened((prev) => !prev);
			setIsShowMessageSearchResult(Boolean(searchText && !isSearchInputOpened));
		},
		[roomID, searchText, isSearchInputOpened]
	);

	useEventHandler(
		EventID.OPEN_SEARCH_RESULT_ON_MINI_CHAT,
		({
			searchValue,
			isOpenSearchResult,
			roomId: targetRoomId
		}: {
			searchValue: string;
			isOpenSearchResult: boolean;
			roomId: string;
		}) => {
			if (targetRoomId !== roomID) return;
			if (isOpenSearchResult) {
				setIsShowMessageSearchResult(Boolean(searchValue));
				if (!searchValue || searchValue.trim().length === 0) return;
				setSearchText(searchValue);
			} else {
				setSearchText("");
				setIsShowMessageSearchResult(false);
			}
		},
		[roomID]
	);

	const handleOnClearSearchInput = () => {
		setIsShowMessageSearchResult(false);
		setSearchText("");
	};

	const handleSearchCompleted = () => {
		setIsSearchInputOpened(false);
		setSearchText("");
		setIsShowMessageSearchResult(false);
	};

	const handleCloseSearchUI = () => {
		setIsSearchInputOpened(false);
		setIsShowMessageSearchResult(false);
	};

	const { messageReaded } = useChatMessageActions();

	const handleShowRecentMessage = useCallback(
		debounce((message) => {
			if (message.isMe) return;
			if (message.roomMessageId === channelData.messageLastReadId) return;
			messageReaded({ channelID: roomID, messageID: message.roomMessageId });
		}, 1000),
		[channelData?.messageLastReadId]
	);

	return (
		<Column
			css={{
				width: "100%",
				height: "100%"
			}}
		>
			<Maybe test={isSearchInputOpened && roomID}>
				<Row
					verticalAlign="center"
					gap={8}
					style={{
						width: "100%",
						padding: "4px 60px 4px 60px",
						backgroundColor:
							isShowMessageSearchResult && Boolean(searchText.trim())
								? colors.accent_10_100
								: colors.neutral_10_100
					}}
				>
					<MessageSearchInConversation
						searchedText={searchText}
						onClear={handleOnClearSearchInput}
						roomId={roomID}
					/>
					<Row gap={8} verticalAlign="center" style={{ paddingRight: 8 }}>
						<Button.Icon
							shape="round"
							name="Close24"
							size="medium_18"
							variant="text"
							onClick={handleCloseSearchUI}
						/>
					</Row>
				</Row>
			</Maybe>

			<Maybe test={!isShowMessageSearchResult && !isSearchInputOpened}>
				<Row style={{ width: "100%", padding: "8px 60px" }} horizonAlign="center">
					<VoiceCallLayer chatSize="full" roomID={channelData.roomId} spaceID={channelData.spaceId} />
				</Row>
			</Maybe>

			<Maybe test={channelData.roomJoined} fallback={<UnauthorizedChannel createdBy={channelData.createdBy} />}>
				{isShowMessageSearchResult && Boolean(searchText.trim()) ? (
					<SearchedResultWrapper>
						<MessageSearchResult
							onComplete={handleSearchCompleted}
							searchText={searchText}
							roomID={roomID}
						/>
					</SearchedResultWrapper>
				) : (
					<MessageList
						isMyRoom={channelData.myRoom}
						variant="fullSize"
						roomID={roomID}
						spaceID={spaceID}
						onShowRecentMessage={handleShowRecentMessage}
						notice={channelData.notice}
						voteNotice={channelData.voteNotice}
					/>
				)}
			</Maybe>

			<Maybe test={channelData.private && channelData.deleted && channelData.roomJoined}>
				<DeletedChannel roomID={channelData.roomId} />
			</Maybe>

			<Maybe test={channelData.roomJoined && !isShowMessageSearchResult}>
				<MessageInput
					id={roomID || ""}
					type="Space"
					disabled={channelData.private && channelData.deleted && channelData.roomJoined}
					userList={
						channelData.users.map((v) => ({ userID: v.id, name: v.name, src: v.profileImagePath })) || []
					}
					variant="fullSize"
					spaceID={spaceID}
					myRoomID={channelData.myRoomId}
					isMyRoom={channelData.myRoom}
				/>
			</Maybe>
		</Column>
	);
}
